import { useState, useEffect } from 'react';
import { FiX, FiSave, FiAlertTriangle } from 'react-icons/fi';
import { useRibCorrection } from '../hooks/useRibCorrection';

const FR_IBAN = /^FR\d{2}[A-Z0-9]{23}$/;
const BIC = /^[A-Z0-9]{8,11}$/;

export default function RibCorrectionModal({ open, onClose, account, onSaved }) {
  const [iban, setIban] = useState('');
  const [bic, setBic]   = useState('');
  const { correct, saving, error } = useRibCorrection();

  useEffect(() => {
    setIban((account?.iban || '').replace(/\s+/g, '').toUpperCase());
    setBic((account?.bic || '').toUpperCase());
  }, [account]);

  if (!open || !account) return null;

  const cleanIban = iban.replace(/\s+/g, '').toUpperCase();
  const cleanBic = (bic || '').trim().toUpperCase();
  const ibanOk = FR_IBAN.test(cleanIban);
  const bicOk  = !cleanBic || BIC.test(cleanBic);

  async function handleSave() {
    try {
      const r = await correct(account.id, { iban: cleanIban, bic: cleanBic || null });
      onSaved?.(r);
      onClose();
    } catch { /* error already in hook */ }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">
            Corriger le RIB — {account.company_name || 'Client'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              IBAN (FR — 27 chars)
            </label>
            <input
              value={iban}
              onChange={(e) => setIban(e.target.value.toUpperCase())}
              className="w-full px-2 py-1.5 text-[12px] font-mono border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="FR76…"
              maxLength={34}
            />
            {!ibanOk && cleanIban && (
              <div className="text-[10px] text-red-600 mt-1 flex items-center gap-1">
                <FiAlertTriangle className="h-3 w-3" /> Format IBAN FR invalide.
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              BIC (8 ou 11 chars, optionnel)
            </label>
            <input
              value={bic}
              onChange={(e) => setBic(e.target.value.toUpperCase())}
              className="w-full px-2 py-1.5 text-[12px] font-mono border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="AGRIFRPP866"
              maxLength={11}
            />
            {!bicOk && (
              <div className="text-[10px] text-red-600 mt-1">Format BIC invalide.</div>
            )}
          </div>
          {error && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
          <div className="text-[10px] text-gray-500">
            La modification est tracée (qui / quand / ancienne valeur).
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] rounded border border-gray-300 hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!ibanOk || !bicOk || saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300"
          >
            <FiSave className="h-3.5 w-3.5" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
